<template>
    <section id="edit-paper">
        <cm-back
        :sectionTitle="isNew ? 'New paper' : 'Edit paper'"
        :contentName="isNew ? 'Create a new paper' : paper.title"
        action="true"
        ></cm-back>
        <div class="wrap-papers-tabs" v-if="!isNew">
            <div class="papers-tabs mobile-only">
                <div
                    class="mobile-tab"
                    :class="{active: !mobileTab}"
                    @click="mobileTab = false"
                >EDIT PAPER</div>
                <div
                    class="mobile-tab"
                    :class="{active: mobileTab}"
                    @click="mobileTab = true"
                >SUBMITTED</div>
            </div>
        </div>
        <div class="container">
            <div class="row" :class="{'justify-content-center': isNew}">
                <div class="col-12 col-lg-8 mt-3" :class="{'mobile-no': mobileTab}">
                    <warning class="mb-3" v-if="!isNew">
                        Changes made here do
                        <b>not apply</b> to papers already submitted to events.
                    </warning>
                    <div class="form-block py-md-3">
                        <h3 class="d-none d-lg-block">Title</h3>
                        <div class="fields-container pt-0">
                            <md-field :class="getValidationClass('title')">
                                <label>Talk title</label>
                                <md-input
                                    v-model="paper.title"
                                    maxlength="120"
                                    :disabled="loading"
                                />
                                <span
                                    class="md-error"
                                    v-if="!$v.paper.title.required"
                                >Title is required</span>
                                <span
                                    class="md-error"
                                    v-if="!$v.paper.title.minLength"
                                >Invalid title</span>
                            </md-field>
                        </div>
                    </div>

                    <div class="form-block py-md-3">
                        <h3 class="d-none d-lg-block">Abstract</h3>
                        <div class="fields-container pt-0">
                            <md-field :class="getValidationClass('abstract')">
                                <label>Abstract</label>
                                <md-textarea
                                    v-model="paper.abstract"
                                    maxlength="600"
                                    :disabled="loading"
                                ></md-textarea>
                                <span
                                    class="md-error"
                                    v-if="!$v.paper.abstract.required"
                                >Abstract is required</span>
                                <span
                                    class="md-error"
                                    v-if="!$v.paper.abstract.minLength"
                                >Invalid abstract</span>
                            </md-field>
                        </div>
                    </div>

                    <div class="form-block py-md-3">
                        <h3 class="d-none d-lg-block">Tags</h3>
                        <div class="fields-container pt-0">
                            <md-chips
                                ref="tags"
                                v-model="tagsInput"
                                md-placeholder="Add a tag and press enter..."
                                :disabled="loading"
                            >
                                <label>Add tags</label>
                                <span class="md-suffix link" @click="addChipAction">Add</span>
                            </md-chips>
                        </div>
                    </div>
                    <div v-if="!isNew">
                        <hr class="w-100" />
                        <div class="w-100 d-flex align-items-center justify-content-end mb-4">
                            <a
                                href
                                class="mr-4"
                                @click.prevent="deleteConfirm = true"
                                v-if="isRemovable"
                            >Remove paper</a>
                            <button
                                class="btn btn-primary align-items-center justify-content-center"
                                @click="updatePaper"
                                :disabled="loading || !dirty"
                            >
                                <b-spinner v-if="loading" variant="light" small class="mr-2"></b-spinner>Update
                            </button>
                        </div>
                    </div>
                    <div v-else>
                        <hr class="w-100" />
                        <div class="w-100 d-flex align-items-center justify-content-end mb-4">
                            <button
                                class="btn btn-primary align-items-center justify-content-center"
                                :disabled="loading"
                                @click="uploadPaper"
                            >
                                <b-spinner v-if="loading" variant="light" small class="mr-2"></b-spinner>save new paper
                            </button>
                        </div>
                    </div>
                </div>
                <div v-if="!isNew" class="col-12 col-lg-4 my-3" :class="{'mobile-no': !mobileTab}">
                    <list-cfs ref="cfsList" type="submitted_to"></list-cfs>
                </div>
            </div>
        </div>
        <md-dialog-alert
            :md-fullscreen="false"
            class="alert-modal"
            :md-active.sync="showResult"
            md-title="Result"
            :md-content="resultMessage"
            @md-closed="handleResultClose"
        />
        <md-dialog-confirm
            :md-active.sync="deleteConfirm"
            :md-fullscreen="false"
            md-title="Are you sure?"
            md-content="This cannot be undone. Are you sure you want to delete this paper?"
            md-confirm-text="Yes, delete it."
            md-cancel-text="No, I changed my mind."
            @md-confirm="deletePaper"
        />
    </section>
</template>

<script>
import Warning from "@/components/Warning.vue";
import CmBack from "@/components/CmBack.vue";
import ListCfs from "@/components/Papers/ListCfs.vue";
import { validationMixin } from "vuelidate";
import {
    required,
    minValue,
    maxValue,
    minLength,
    maxLength,
    requiredIf
} from "vuelidate/lib/validators";
export default {
    name: "edit-paper",

    components: {
        Warning,
        ListCfs,
        CmBack
    },
    computed: {
        isNew() {
            return this.$route.name === "new-paper";
        },
        isRemovable() {
            if (!this.loading) {
                return !this.$refs.cfsList.$data.proposals.length;
            }
            return false;
        }
    },
    data: () => ({
        mobileTab: false,
        paper: {
            title: "",
            abstract: "",
            tags: ""
        },
        tagsInput: [],
        newId: "",
        showResult: false,
        resultMessage: "",
        loading: true,
        dirty: false,
        deleteConfirm: false,
        deleteSuccess: false
    }),

    watch: {
        "paper.title"() {
            this.setDirty();
        },
        "paper.abstract"() {
            this.setDirty();
        },
        tagsInput() {
            this.setDirty();
        }
    },

    async mounted() {
        this.loading = true;
        if (!this.isNew) {
            this.newId = "";
            try {
                this.paper = await this.$papersApi.getPaper(
                    this.$route.params.paperId
                );
                this.tagsInput = this.paper.tags
                    ? this.paper.tags.split(",")
                    : [];
                this.dirty = false;
            } catch (error) {
                console.error(error);
                this.$router.replace({ name: "papers" });
            }
        }
        this.loading = false;
    },
    validations: {
        paper: {
            title: {
                required,
                minLength: minLength(3)
            },
            abstract: {
                required,
                minLength: minLength(3)
            }
        }
    },
    mixins: [validationMixin],
    methods: {
        handleResultClose() {
            if (this.newId) {
                this.$router.replace({
                    name: "papers"
                });
                // this.$router.replace({
                //     name: "edit-paper",
                //     params: { paperId: this.newId }
                // });
                // this.renderKey++;
            } else if (this.deleteSuccess) {
                this.$router.replace({
                    name: "papers"
                });
            }
        },
        setDirty() {
            this.dirty = true;
        },
        addChipAction() {
            this.$refs.tags.insertChip(this.$refs.tags);
        },
        getValidationClass(fieldName) {
            const field = this.$v.paper[fieldName];
            if (field) {
                return {
                    "md-invalid": field.$invalid && field.$dirty
                };
            }
        },
        async uploadPaper() {
            if (this.tagsInput) {
                this.$refs.tags.insertChip(this.$refs.tags);
            }
            this.$v.$touch();
            if (this.$v.$invalid) {
                this.resultMessage = "There are some errors. Check the form.";
                this.showResult = true;
                return;
            }
            try {
                this.loading = true;
                const res = await this.$papersApi.insertPaper({
                    ...this.paper,
                    tags: this.tagsInput.toString()
                });
                this.newId = res.data.paper.id;
                this.resultMessage = "Paper successfully created!";
            } catch (error) {
                this.resultMessage =
                    "Something went wrong. Error: " + error.data.message;
                console.error(error);
            } finally {
                this.loading = false;
                this.showResult = true;
            }
        },
        async updatePaper() {
            if (this.tagsInput) {
                this.$refs.tags.insertChip(this.$refs.tags);
            }
            this.$v.$touch();
            if (this.$v.$invalid) {
                this.resultMessage = "There are some errors. Check the form.";
                this.showResult = true;
                return;
            }
            try {
                this.loading = true;
                const res = await this.$papersApi.updatePaper({
                    ...this.paper,
                    tags: this.tagsInput.toString()
                });
                this.resultMessage = "Paper successfully updated!";
            } catch (error) {
                this.resultMessage =
                    "Something went wrong. Error: " + error.data.message;
                console.error(error);
            } finally {
                this.loading = false;
                this.showResult = true;
            }
        },
        async deletePaper() {
            try {
                this.loading = true;
                await this.$papersApi.deletePaper(this.$route.params.paperId);
                this.resultMessage = "Paper successfully deleted.";
                this.deleteSuccess = true;
            } catch (error) {
                this.resultMessage =
                    "Something went wrong. Error: " + error.data.message;
                console.error(error);
            } finally {
                this.loading = false;
                this.showResult = true;
            }
        }
    }
};
</script>

<style lang="scss">
@import "@/assets/scss/_common.scss";

#edit-paper {
    .md-chips {
        position: relative;
        padding-right: 35px;
        .md-suffix {
            position: absolute;
            right: 0;
        }
    }

    .md-chip.md-theme-default .md-icon-image svg {
        cursor: pointer;
        fill: $palette-blue !important;
        color: $palette-blue;
    }

    .md-chips.md-field .md-chip {
        margin-right: 8px;
    }

    .md-chip.md-theme-default .md-icon.md-icon-image svg {
        fill: $palette-blue !important;
        color: $palette-blue;
    }

    .md-field.md-has-placeholder .md-input,
    .md-field.md-has-placeholder .md-textarea {
        font-weight: 500 !important;
    }
    .md-suffix.link {
        color: $palette-orange !important;
        bottom: 0;
    }
    label {
        margin: 0 !important;
    }
}
</style>
<style lang="scss" scoped>
@import "@/assets/scss/_common.scss";

#edit-paper {
    .form-block {
        width: 100%;
        padding-bottom: 20px;
        // input {
        //     margin-top: 10px;
        // }
        @media all and (min-width: $size-tablet-landscape) {
            display: flex;
            max-width: 100%;
            & > h3 {
                padding-right: 15px;
            }
        }
        h3 {
            text-align: left;
            font-size: 16px;
            padding-bottom: 10px;
            margin-top: 10px;
            border-bottom: 0px solid #e4e4ea;
            @media all and (min-width: $size-tablet-landscape) {
                width: 20%;
                border: none;
                border-right: 2px solid #e4e4ea;
                margin: 0;
                padding: 10px 0;
            }
        }
    }

    .fields-container {
        display: flex;
        flex-direction: column;
        padding-top: 5px;
        @media all and (min-width: $size-tablet-landscape) {
            padding-left: 25px;
            width: 80%;
        }

        h3 {
            margin-left: 20px;
            border: none;
            small {
                display: block;
            }
        }
        .delete-button {
            padding-top: 16px;
            display: flex;
            min-height: 48px;
            position: relative;
            margin: 4px 0 0;
        }
    }

    .fields-container {
        i {
            position: relative;
            top: 12px;
        }
    }

    .md-textarea {
        min-height: 200px !important;
    }

    .md-has-textarea {
        background: white;
    }

    .wrap-papers-tabs {
        border-bottom: 1px solid #ededed;
    }
    @media all and (max-width: 991px) {
        .papers-tabs .active {
            color: #ff5c00;
            border-color: #ff5c00;
        }

        .papers-tabs {
            display: flex !important;
            width: 100%;
            // margin-bottom: 10px;
            padding: 0 10px;

            .papers-tabs .mobile-tab {
                width: 50%;
                text-align: center;
                border-bottom: 2px solid transparent;
                -webkit-transition: all 0.5s ease;
                transition: all 0.5s ease;
                padding: 10px 15px;
                cursor: pointer;
                font-weight: 600;
            }
            .mobile-tab {
                width: 50%;
                text-align: center;
                border-bottom: 2px solid transparent;
                transition: all 0.5s ease;
                padding: 10px 15px;
                cursor: pointer;
                font-weight: 600;
            }
            .active {
                color: #ff5c00;
                border-color: #ff5c00;
            }
        }
    }
}
</style>